import React from 'react';
import { Upload, FileSpreadsheet, Loader2, Trash2, AlertTriangle, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';
import { toast } from 'sonner';

function StatusBadge({ status }) {
  const config = {
    uploaded: { label: 'Uploaded', cls: 'text-blue-600 border-blue-200' },
    parsed: { label: 'Parsed', cls: 'text-blue-600 border-blue-200' },
    mapped: { label: 'Mapped', cls: 'text-violet-600 border-violet-200' },
    validated: { label: 'Validated', cls: 'text-emerald-600 border-emerald-200' },
    confirmed: { label: 'Confirmed', cls: 'text-emerald-700 border-emerald-300' },
    error: { label: 'Error', cls: 'text-red-600 border-red-200' },
  };
  const c = config[status] || config.error;
  return <Badge variant="outline" className={`text-xs ${c.cls}`}>{c.label}</Badge>;
}

export function ImportUploadForm({
  file, setFile, fiscalYear, setFiscalYear, importType, setImportType,
  processing, onUpload,
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2"><FileSpreadsheet className="w-5 h-5 text-[#D94E18]" />Import Historical Data</CardTitle>
        <CardDescription>Upload a Trial Balance or Financial Statements from a prior fiscal year to establish opening balances. Supports CSV, XLSX, and PDF.</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
          <div className="space-y-1.5">
            <Label>Fiscal Year</Label>
            <Input type="number" min={2000} max={new Date().getFullYear()} value={fiscalYear}
              onChange={(e) => setFiscalYear(parseInt(e.target.value) || new Date().getFullYear() - 1)} data-testid="fiscal-year-input" />
          </div>
          <div className="space-y-1.5">
            <Label>Import Type</Label>
            <select value={importType} onChange={(e) => setImportType(e.target.value)} className="w-full h-9 rounded-md border border-zinc-200 px-3 text-sm" data-testid="import-type-select">
              <option value="trial_balance">Trial Balance</option>
              <option value="financial_statements">Financial Statements</option>
            </select>
          </div>
        </div>

        <div
          className={`relative border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors mb-4 ${
            file ? 'border-emerald-300 bg-emerald-50/50' : 'border-zinc-300 bg-zinc-50/50 hover:border-[#D94E18] hover:bg-orange-50/30'
          }`}
          onClick={() => document.getElementById('historical-file-input')?.click()}
          onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('border-[#D94E18]', 'bg-orange-50/30'); }}
          onDragLeave={(e) => { e.preventDefault(); e.currentTarget.classList.remove('border-[#D94E18]', 'bg-orange-50/30'); }}
          onDrop={(e) => {
            e.preventDefault();
            e.currentTarget.classList.remove('border-[#D94E18]', 'bg-orange-50/30');
            const droppedFile = e.dataTransfer.files?.[0];
            if (droppedFile) {
              const ext = droppedFile.name.split('.').pop().toLowerCase();
              if (['csv', 'xlsx', 'pdf'].includes(ext)) setFile(droppedFile);
              else toast.error('Unsupported file type. Use CSV, XLSX, or PDF.');
            }
          }}
          data-testid="file-drop-zone"
        >
          <input id="historical-file-input" type="file" accept=".csv,.xlsx,.pdf" onChange={(e) => setFile(e.target.files?.[0] || null)} className="hidden" data-testid="file-input" />
          {file ? (
            <div className="flex items-center justify-center gap-3">
              <FileSpreadsheet className="w-8 h-8 text-emerald-600" />
              <div className="text-left">
                <p className="text-sm font-medium text-zinc-900">{file.name}</p>
                <p className="text-xs text-zinc-500">{(file.size / 1024).toFixed(1)} KB — Click or drop to change</p>
              </div>
              <button type="button" onClick={(e) => { e.stopPropagation(); setFile(null); }} className="ml-2 p-1.5 rounded-md text-zinc-400 hover:text-red-500 hover:bg-red-50 transition-colors" data-testid="remove-file-btn" title="Remove file">
                <X className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <>
              <Upload className="w-8 h-8 text-zinc-400 mx-auto mb-2" />
              <p className="text-sm font-medium text-zinc-700">Click to select or drag & drop your file</p>
              <p className="text-xs text-zinc-400 mt-1">Supports CSV, XLSX, and PDF</p>
            </>
          )}
        </div>

        <Button onClick={onUpload} disabled={processing || !file} className="bg-[#D94E18] hover:bg-[#C2410C] text-white" data-testid="upload-historical-btn">
          {processing ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Upload className="w-4 h-4 mr-1.5" />}Upload & Parse
        </Button>
        {!file && <p className="text-xs text-zinc-400 mt-2">Select a file above to enable upload</p>}
      </CardContent>
    </Card>
  );
}

export function ImportList({ imports, onResume, onDelete, deleteTarget, setDeleteTarget, onConfirmDelete }) {
  if (imports.length === 0) return null;
  return (
    <>
      <Card>
        <CardHeader><CardTitle className="text-base">Previous Imports</CardTitle></CardHeader>
        <CardContent>
          <div className="divide-y divide-zinc-100">
            {imports.map((imp) => (
              <div key={imp.id} className="flex items-center justify-between py-3 gap-3" data-testid={`import-row-${imp.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium text-zinc-900">FY {imp.fiscal_year}</span>
                    <Badge variant="outline" className="text-xs">{imp.import_type.replace('_', ' ')}</Badge>
                    <StatusBadge status={imp.status} />
                  </div>
                  <p className="text-xs text-zinc-400 mt-0.5 truncate">{imp.filename} — {imp.parsed_rows} accounts</p>
                </div>
                <div className="flex gap-1.5 flex-shrink-0">
                  {imp.status !== 'confirmed' && (
                    <>
                      <Button variant="outline" size="sm" onClick={() => onResume(imp)} className="text-xs" data-testid={`resume-import-${imp.id}`}>Resume</Button>
                      <Button variant="ghost" size="sm" onClick={() => onDelete(imp)} className="text-red-500 hover:text-red-700 hover:bg-red-50" data-testid={`delete-import-${imp.id}`}><Trash2 className="w-4 h-4" /></Button>
                    </>
                  )}
                  {imp.status === 'confirmed' && <Badge className="bg-emerald-600 text-xs">Posted</Badge>}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-red-500" /> Delete Import?</AlertDialogTitle>
            <AlertDialogDescription>Delete the FY {deleteTarget?.fiscal_year} import ({deleteTarget?.filename})? This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={onConfirmDelete} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-import-btn">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
